import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Logo } from '../../components/brand/Logo'
import { Icon } from '../../components/ui/icons'
import { DocumentIdentityFooter } from '../../components/print/DocumentIdentityFooter'

interface CertificateInfo {
  valid: boolean
  certificateCode?: string
  workerName?: string
  workerRut?: string
  battery?: string
  jobTitle?: string | null
  company?: string | null
  result?: 'FIT' | 'FIT_WITH_RESTRICTIONS' | 'UNFIT' | 'PENDING'
  completedAt?: string
}

const RESULT: Record<string, { label: string; className: string }> = {
  FIT: { label: 'APTO', className: 'bg-emerald-50 text-emerald-800 border-emerald-500' },
  FIT_WITH_RESTRICTIONS: { label: 'APTO CON RESTRICCIONES', className: 'bg-amber-50 text-amber-800 border-amber-400' },
  UNFIT: { label: 'NO APTO', className: 'bg-red-50 text-red-700 border-red-400' },
  PENDING: { label: 'PENDIENTE', className: 'bg-gray-50 text-gray-600 border-gray-300' },
}

/** Verificación pública de certificados preocupacionales por código. */
export function VerifyCertificatePage() {
  const [searchParams] = useSearchParams()
  const [code, setCode] = useState(searchParams.get('codigo') ?? '')
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [info, setInfo] = useState<CertificateInfo | null>(null)

  async function verifyCode(value: string) {
    setState('loading')
    try {
      const { data } = await axios.get<CertificateInfo>(`/api/public/certificates/${encodeURIComponent(value.trim())}`)
      setInfo(data)
      setState('done')
    } catch {
      setState('error')
    }
  }

  // Verificación directa al llegar con ?codigo= (enlaces del portal y de los emails).
  useEffect(() => {
    const fromUrl = searchParams.get('codigo')
    if (fromUrl) void verifyCode(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function verify(e: FormEvent) {
    e.preventDefault()
    await verifyCode(code)
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b border-steel-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label="Cima Sport"><Logo className="h-9" /></Link>
          <Link to="/" className="text-sm font-medium text-brand-700 hover:underline">← Volver al sitio</Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12">
        <h1 className="font-display text-2xl font-bold text-ink">Verificación de certificados</h1>
        <p className="mt-1 text-gray-600">
          Ingresa el código impreso en el certificado preocupacional para validar su autenticidad.
        </p>

        <form onSubmit={verify} className="mt-6 flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="p. ej. CS-1A2B3C4D5E"
            required
            className="w-full rounded-lg border border-steel-200 bg-white px-4 py-3 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            className="rounded-full bg-brand-600 px-6 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {state === 'loading' ? 'Verificando…' : 'Verificar'}
          </button>
        </form>

        {state === 'error' && (
          <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">No pudimos verificar, intenta de nuevo.</p>
        )}

        {state === 'done' && info && !info.valid && (
          <div className="mt-6 rounded-2xl border-2 border-red-300 bg-red-50 p-6 text-center">
            <p className="flex items-center justify-center gap-2 font-display text-xl font-bold text-red-700">
              <Icon name="close" className="h-5 w-5" />
              Código no válido
            </p>
            <p className="mt-1 text-sm text-red-600">
              El código no corresponde a un certificado emitido por Cima Sport, o el certificado no está completado.
            </p>
          </div>
        )}

        {state === 'done' && info?.valid && (
          <div className={`print-document mt-6 rounded-2xl border-2 p-6 ${RESULT[info.result ?? 'PENDING'].className}`}>
            <p className="text-center font-display text-2xl font-bold">{RESULT[info.result ?? 'PENDING'].label}</p>
            <dl className="mx-auto mt-4 max-w-md space-y-1.5 text-sm text-ink">
              <div className="flex justify-between gap-4"><dt className="font-medium">Código</dt><dd className="font-mono">{info.certificateCode}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">Trabajador</dt><dd>{info.workerName}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">RUT</dt><dd className="font-mono">{info.workerRut}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">Batería</dt><dd>{info.battery}</dd></div>
              {info.jobTitle && <div className="flex justify-between gap-4"><dt className="font-medium">Cargo</dt><dd>{info.jobTitle}</dd></div>}
              {info.company && <div className="flex justify-between gap-4"><dt className="font-medium">Empresa</dt><dd>{info.company}</dd></div>}
              {info.completedAt && (
                <div className="flex justify-between gap-4">
                  <dt className="font-medium">Fecha</dt>
                  <dd>{new Date(info.completedAt).toLocaleDateString('es-CL')}</dd>
                </div>
              )}
            </dl>
            <p className="mt-4 text-center text-xs text-gray-500">
              Certificado verificado contra los registros de Cima Sport.
            </p>
            <DocumentIdentityFooter verificationCode={info.certificateCode} kind="certificate" />
            <div className="mt-4 text-center print:hidden">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-full border border-steel-300 bg-white px-4 py-2 text-sm font-medium text-ink hover:bg-soft"
              >
                <Icon name="print" className="h-4 w-4" />
                Imprimir / guardar PDF
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
